/* eslint-disable @typescript-eslint/no-unused-expressions */
import { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
// import { Viewer, ViewerOptions } from 'photo-sphere-viewer';
import 'photo-sphere-viewer/dist/photo-sphere-viewer.css';
import '@photo-sphere-viewer/markers-plugin/index.css';
import '@photo-sphere-viewer/core/index.css';

export type SphereViewerOptions = Omit<ViewerConfig, 'container' | 'panorama'>;
// import { MarkersPlugin } from 'photo-sphere-viewer/dist/plugins/markers';
import { Viewer, ViewerConfig } from '@photo-sphere-viewer/core';
// import { ViewerOptions } from 'photo-sphere-viewer';
import { Marker, MarkersPlugin } from '@photo-sphere-viewer/markers-plugin';
import { MarkersGeneralProps, MarkersProps, markers } from 'utils/markers';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { MarkerTooltipFooter } from 'components/MarkerTooltip';
import { selectUiState, setUi } from 'store/uiSlice';
import { createPortal } from 'react-dom';

import { selectRoomState, selectSelectedRoomOptions } from 'store/roomSlice';
import { useTranslation } from 'hooks/useTranslation';
import { useGoogleTagManager } from '@grace-studio/graceful-next/hooks';

// import { convertToSlug } from 'utils';

type SphereViewerProps = SphereViewerOptions & {
  imageSrc: string;
  onImageLoadStart?: () => void;
  onImageLoadEnd?: () => void;
  onImageLoadProgress?: (progress: number) => void;
};

const SphereViewer: FC<SphereViewerProps> = ({
  imageSrc,
  onImageLoadEnd,
  onImageLoadStart,
  onImageLoadProgress,
  ...options
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentImage, setCurrentImage] = useState('');
  const [panoramaViewer, setPanoramaViewer] = useState<Viewer | null>(null);
  const [viewerIsReady, setViewerIsReady] = useState<boolean>(false);
  const [blacklist, setBlacklist] = useState<string[]>([]);
  const [whitelist, setWhitelist] = useState<string[]>([]);
  const [activeTooltip, setActiveTooltip] = useState<any>();
  const translation = useTranslation();
  const gtm = useGoogleTagManager();

  const room = useAppSelector(selectRoomState) || {};
  const roomOptions = useAppSelector(selectSelectedRoomOptions) || {};

  const markersGeneral = {
    rooms: {
      openOffice: translation('hotspots.rooms.openOffice'),
      individualOffice: translation('hotspots.rooms.individualOffice'),
      conferenceRoom: translation('hotspots.rooms.conferenceRoom'),
      classroom: translation('hotspots.rooms.classroom'),
    },
    solutions: {
      recessed: {
        recessed: 'recessed',
        'recessed-recessed': 'recessed-recessed',
        'recessed-downlight': 'recessed-downlight',
        downlight: 'downlight',
        linear: 'recessed-linear',
        linearTw: 'recessed-linear-tw',
        'linear-tw': 'recessed-linear-tw',
        'linear-tunable-white': 'recessed-linear-tunable-white',
        'linear-with-spotlights': 'recessed-linear-with-spotlights',
        'linear-with-spotlights-pendants':
          'recessed-linear-with-spotlights-pendants',
        'tunable-white': 'recessed-tunable-white',
        'with-spotlights': 'recessed-with-spotlights',
        'with-spotlights-accent': 'recessed-with-spotlights-and-accent',

        'recessed-linear-with-spotlights':
          'recessed-recessed-linear-with-spotlights',
        'linear-with-spotlights-and-pendants':
          'recessed-recessed-linear-with-spotlights-and-pendants',
      },
      pendant: {
        pendant: 'pendant',
        'pendant-pendant': 'pendant-pendant',
        'ddl-tunable-white-with-wallwasher-accent':
          'pendant-pendant-ddl-tunable-white-with-wallwasher-accent',
        'ddl-wallwasher': 'pendant-pendant-ddl-and-wallwasher',
        'tunable-white': 'pendant-pendant-tunable-white',
        'with-downlight': 'pendant-pendant-with-downlight',
        'with-downlight-tunable-white':
          'pendant-pendant-with-downlight-tunable-white',
        'with-spotlights': 'pendant-pendant-with-spotlights',
        'with-wallwasher-accent': 'pendant-pendant-with-wallwasher-and-accent',
        'with-wallwasher-downlights': 'pendant-pendant-ddl-and-wallwasher',
        'with-wallwasher-downlights-tunable-white':
          'pendant-pendant-ddl-tunable-white-with-wallwasher-and-accent',
        'wallwasher-ddl-accent-tunable-white':
          'pendant-pendant-ddl-tunable-white-with-wallwasher-and-accent',

        'pendant-with-downlight': 'pendant-with-downlight',
        'pendant-with-downlight-tunable-white':
          'pendant-with-downlight-tunable-white',
        'pendant-with-spotlights': 'pendant-with-spotlights',
        'pendant-with-wallwasher-and-downlight':
          'pendant-with-wallwasher-and-downlight',
        'pendant-pendant-with-wallwasher-and-downlight':
          'pendant-pendant-with-wallwasher-and-downlight',
        'pendant-with-wallwasher-and-downlight-tunable-white':
          'pendant-with-wallwasher-and-downlight-tunable-white',
        'pendant-pendant-with-wallwasher-and-downlight-tunable-white':
          'pendant-pendant-with-wallwasher-and-downlight-tunable-white',
      },
    },
    texts: {
      recessed: {
        title: translation('hotspots.recessed.title'),
        text: translation('hotspots.recessed.text'),
      },
      pendant: {
        title: translation('hotspots.pendant.title'),
        text: translation('hotspots.pendant.text'),
      },
      spotlight: {
        title: translation('hotspots.spotlight.title'),
        text: translation('hotspots.spotlight.text'),
      },
      wallwasher: {
        title: translation('hotspots.wallwasher.title'),
        text: translation('hotspots.wallwasher.text'),
      },
      ddl: {
        title: translation('hotspots.ddl.title'),
        text: translation('hotspots.ddl.text'),
      },
      downlight: {
        title: translation('hotspots.downlight.title'),
        text: translation('hotspots.downlight.text'),
      },
      tunableWhite: {
        title: translation('hotspots.tunableWhite.title'),
        text: translation('hotspots.tunableWhite.text'),
      },
      accent: {
        title: translation('hotspots.accent.title'),
        text: translation('hotspots.accent.text'),
      },
    },
  } as MarkersGeneralProps;

  const markersData = markers.data(markersGeneral);
  const markersConfig = markers.config(markersData);

  const currentRoomMarkers = markersConfig.filter(
    (_markers) =>
      // _markers.markers.relation.room == convertToSlug(room?.slug?.current),
      _markers.markers.relation.room == room?.slug?.current.toLowerCase(),
  );
  // console.log('currentRoomMarkers', currentRoomMarkers);

  const dispatch = useAppDispatch();
  const ui = useAppSelector(selectUiState) || {};

  const hasMatchingChoices = (_marker: MarkersProps) => {
    const selectedSolutionSlug = roomOptions.lightingSolution?.slug.current;
    // console.log('SELECTED', selectedSolutionSlug);

    const hasMatch = selectedSolutionSlug
      ? _marker.markers.relation.choice.includes(selectedSolutionSlug)
      : true;

    return hasMatch;
  };

  useEffect(() => {
    setTimeout(() => {
      const notRelevantMarkers = currentRoomMarkers
        .filter((_marker) => !hasMatchingChoices(_marker))
        .map((_marker) => markers.getMarkerID(_marker));
      const relevantMarkers = currentRoomMarkers
        .filter((_marker) => hasMatchingChoices(_marker))
        .map((_marker) => markers.getMarkerID(_marker));

      setBlacklist(notRelevantMarkers);
      setWhitelist(relevantMarkers);
    }, 300);
  }, [roomOptions?.lightingSolution?.slug?.current]);

  useEffect(() => {
    if (containerRef.current && imageSrc) {
      if (imageSrc === currentImage) {
        return;
      }

      onImageLoadStart && onImageLoadStart();

      if (panoramaViewer) {
        panoramaViewer.setPanorama(imageSrc);
        setCurrentImage(imageSrc);
      } else {
        const viewer = new Viewer({
          container: containerRef.current,
          panorama: imageSrc,
          ...options,
          plugins: [[MarkersPlugin, {}]],
        });

        viewer.addEventListener('panorama-loaded', () => {
          onImageLoadEnd && onImageLoadEnd();
        });

        viewer.addEventListener('load-progress', (event) => {
          // const [value] = event.args;
          const { progress } = event;
          onImageLoadProgress && onImageLoadProgress(progress);

          const PROGRESS_END = 100;
          if (progress === PROGRESS_END) {
            onImageLoadEnd && onImageLoadEnd();
          }
        });

        viewer.addEventListener(
          'ready',
          () => {
            setViewerIsReady(true);
          },
          { once: true },
        );

        setPanoramaViewer(viewer);
        setCurrentImage(imageSrc);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSrc]);

  useEffect(() => {
    if (!ui?.tooltip) {
      const markersPlugin = panoramaViewer?.getPlugin(
        MarkersPlugin,
      ) as MarkersPlugin;
      markersPlugin?.hideAllTooltips();
    }
  }, [ui?.tooltip]);

  useEffect(() => {
    if (panoramaViewer && viewerIsReady) {
      const markersPlugin = panoramaViewer?.getPlugin(
        MarkersPlugin,
      ) as MarkersPlugin;

      setTimeout(() => {
        markersPlugin?.addEventListener('select-marker', ({ marker }) => {
          const markerConfig = marker.config as Marker['config'] & {
            title: string;
          };
          gtm.trackEvent({
            event: 'open_hotspot',
            eventInfo: {
              name: markerConfig.title,
            },
          });
          setTimeout(() => {
            const tooltip = document.querySelector(
              '.custom-tooltip .psv-tooltip-content',
            );
            setActiveTooltip(tooltip);
          }, 100);

          const rect = marker.domElement.getBoundingClientRect();

          dispatch(
            setUi({
              ...ui,
              tooltip: {
                content: {
                  title: '',
                  text: `ID: ${marker.config.id}`,
                },
                position: rect.toJSON(),
                ligtingSolutionSectionId: 'accent-lights',
              },
            }),
          );
        });

        markersPlugin.setMarkers(markers.list(currentRoomMarkers, {}));
        markersPlugin?.hideAllMarkers();
      }, 1000);
    }
  }, [panoramaViewer, viewerIsReady]);

  const toggleMarkers = () => {
    const markersPlugin = panoramaViewer?.getPlugin(
      MarkersPlugin,
    ) as MarkersPlugin;

    if (markersPlugin) {
      // console.log('now hide', blacklist);

      // markersPlugin?.showAllMarkers();
      // blacklist?.map((id) => {
      //   markersPlugin?.hideMarker(id);
      // });
      markersPlugin?.hideAllMarkers();

      whitelist?.map((id) => {
        markersPlugin?.showMarker(id);
      });
    }
  };

  useEffect(() => {
    if (panoramaViewer && viewerIsReady) {
      setTimeout(() => {
        toggleMarkers();
      }, 1000);
    }
  }, [blacklist]);

  return (
    <>
      {activeTooltip && createPortal(<MarkerTooltipFooter />, activeTooltip)}

      <div ref={containerRef} className="h-full" />
    </>
  );
};

export default SphereViewer;
